import React from 'react';
import {
	Alert,
	Box,
	Button,
	Chip,
	Grid,
	InputAdornment,
	MenuItem,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import { REMINDER_INTERVAL_OPTIONS, VENMO_MAX_AMOUNT } from '../constants';
import {
	PreviewPanel,
	SectionCard,
	SectionHeader,
} from '../components/SettingsShell';

const PaymentsSection = ({
	formData,
	setFormData,
	saving,
	stripeReady,
	stripeConnected,
	connecting,
	stripeStatusMessage,
	paymentPreview,
	venmoPreview,
	emailFooterPreview,
	sampleEmailTooltip,
	sendingSampleBid,
	sendingSampleInvoice,
	userEmail,
	onInputChange,
	onSave,
	onConnectStripe,
	onSendSampleBid,
	onSendSampleInvoice,
}) => (
	<Stack spacing={{ xs: 2.5, sm: 3 }}>
		<SectionCard>
			<SectionHeader
				icon={PaymentsOutlinedIcon}
				title="Payment methods"
				description="How customers can pay... check, Venmo, and card via Stripe."
			/>

			<Box component="form" onSubmit={onSave} noValidate>
				<Stack spacing={{ xs: 2.5, sm: 3 }}>
					<TextField
						label="Venmo username"
						name="venmo_username"
						value={formData.venmo_username}
						onChange={onInputChange}
						fullWidth
						placeholder="your-business"
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">@</InputAdornment>
							),
						}}
						helperText={`Optional. Shown on deposit and invoice emails when the amount is $${VENMO_MAX_AMOUNT.toLocaleString()} or less.`}
						sx={{ maxWidth: { sm: 420 } }}
					/>

					<Box
						sx={{
							display: 'flex',
							justifyContent: { xs: 'stretch', sm: 'flex-start' },
						}}
					>
						<Button
							type="submit"
							variant="contained"
							disabled={saving}
							sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 160 } }}
						>
							{saving ? 'Saving...' : 'Save Venmo settings'}
						</Button>
					</Box>
				</Stack>
			</Box>
		</SectionCard>

		<SectionCard>
			<Box component="form" onSubmit={onSave} noValidate>
				<Stack spacing={1.5}>
					<Typography variant="subtitle1" fontWeight={700}>
						Invoice reminders
					</Typography>
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ lineHeight: 1.6 }}
					>
						Email customers a nudge to review an unpaid invoice on this cadence
						while a balance is still due. Your organization email is BCC&apos;d,
						and each send appears in the job activity feed.
					</Typography>
					<TextField
						select
						label="Reminder frequency"
						name="invoice_reminder_interval_days"
						value={formData.invoice_reminder_interval_days}
						onChange={(event) =>
							setFormData((prev) => ({
								...prev,
								invoice_reminder_interval_days: Number(event.target.value),
							}))
						}
						sx={{ maxWidth: { sm: 280 } }}
					>
						{REMINDER_INTERVAL_OPTIONS.map((option) => (
							<MenuItem key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</TextField>
					<Box
						sx={{
							display: 'flex',
							justifyContent: { xs: 'stretch', sm: 'flex-start' },
						}}
					>
						<Button
							type="submit"
							variant="contained"
							disabled={saving}
							sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 160 } }}
						>
							{saving ? 'Saving...' : 'Save reminder settings'}
						</Button>
					</Box>
				</Stack>
			</Box>
		</SectionCard>

		{!stripeReady ? (
			<SectionCard>
				<Stack spacing={2}>
					<Typography variant="subtitle1" fontWeight={700}>
						Stripe card payments
					</Typography>
					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto' },
							gap: 2,
							alignItems: { md: 'center' },
						}}
					>
						<Alert severity={stripeConnected ? 'warning' : 'info'}>
							{stripeStatusMessage}
						</Alert>
						<Button
							variant="contained"
							onClick={onConnectStripe}
							disabled={connecting}
							sx={{ width: { xs: '100%', md: 'auto' }, flexShrink: 0 }}
						>
							{connecting
								? 'Opening Stripe...'
								: stripeConnected
									? 'Continue Stripe setup'
									: 'Connect Stripe'}
						</Button>
					</Box>
				</Stack>
			</SectionCard>
		) : null}

		<SectionCard>
			<Stack spacing={0.75} sx={{ mb: 2 }}>
				<Typography variant="subtitle1" fontWeight={700}>
					Customer-facing previews
				</Typography>
				<Typography variant="body2" color="text.secondary">
					How check, Venmo, and email footer details will look to customers.
				</Typography>
			</Stack>

			<Grid container spacing={{ xs: 2, sm: 2.5 }}>
				<Grid item xs={12} md={4}>
					<PreviewPanel
						title="Check instructions"
						action={
							<Tooltip title={sampleEmailTooltip}>
								<span>
									<Button
										size="small"
										variant="outlined"
										startIcon={<EmailOutlinedIcon />}
										onClick={onSendSampleInvoice}
										disabled={sendingSampleInvoice || !userEmail}
										sx={{ width: { xs: '100%', sm: 'auto' } }}
									>
										{sendingSampleInvoice ? 'Sending...' : 'Example invoice'}
									</Button>
								</span>
							</Tooltip>
						}
					>
						<Typography variant="body2" sx={{ lineHeight: 1.75 }}>
							{paymentPreview}
						</Typography>
					</PreviewPanel>
				</Grid>
				<Grid item xs={12} md={4}>
					<PreviewPanel title="Venmo payment">
						<Stack spacing={1.5}>
							<Typography variant="body2" sx={{ lineHeight: 1.75 }}>
								{venmoPreview}
							</Typography>
							{formData.venmo_username.trim() ? (
								<Chip
									component="a"
									href="https://account.venmo.com/pay"
									target="_blank"
									rel="noopener noreferrer"
									icon={<AccountBalanceWalletOutlinedIcon />}
									label={`Pay with Venmo @${formData.venmo_username.trim().replace(/^@+/, '')}`}
									clickable
									sx={{
										alignSelf: 'flex-start',
										bgcolor: '#3d95ce',
										color: 'common.white',
										fontWeight: 700,
										'& .MuiChip-icon': { color: 'common.white' },
										'&:hover': { bgcolor: '#3586ba' },
									}}
								/>
							) : null}
						</Stack>
					</PreviewPanel>
				</Grid>
				<Grid item xs={12} md={4}>
					<PreviewPanel
						title="Email footer"
						action={
							<Tooltip title={sampleEmailTooltip}>
								<span>
									<Button
										size="small"
										variant="outlined"
										startIcon={<EmailOutlinedIcon />}
										onClick={onSendSampleBid}
										disabled={sendingSampleBid || !userEmail}
										sx={{ width: { xs: '100%', sm: 'auto' } }}
									>
										{sendingSampleBid ? 'Sending...' : 'Example bid'}
									</Button>
								</span>
							</Tooltip>
						}
					>
						<Typography
							variant="body2"
							sx={{ lineHeight: 1.75, whiteSpace: 'pre-line' }}
						>
							{emailFooterPreview}
						</Typography>
					</PreviewPanel>
				</Grid>
			</Grid>
		</SectionCard>
	</Stack>
);

export default PaymentsSection;
